import type { PreviewKeys } from './previewKey';

/**
 * Die Entschlüsselung einer Preview nach dem Vertrag mit Message (2026-10-25).
 *
 * ⭐ **Übernommen von Ink** (`INK/src/preview/previewCrypto.ts`, `42c3fef`), nur im Stil von
 * Stage. Beide Apps holen bei demselben Dienst ab; weicht eine Seite ab, merkt es erst der
 * Autor, dessen Preview nicht aufgeht.
 */

/** Warum eine Preview nicht kommt — je Grund ein Satz für den Prüfer. */
export type PreviewReason =
  | 'notFound'
  | 'network'
  | 'clock'
  | 'signature'
  | 'decrypt'
  | 'version'
  | 'tooLarge'
  | 'format'
  | 'realExam';

export class PreviewError extends Error {
  constructor(
    readonly reason: PreviewReason,
    detail?: string,
  ) {
    super(detail ? `${reason}: ${detail}` : reason);
  }
}

export interface PreviewFile {
  id: string;
  name: string;
  mimeType: string;
  metadata: Record<string, string>;
}

export interface PreviewHeader {
  from: string;
  metadata: Record<string, string>;
  files: PreviewFile[];
}

/** WebCrypto nimmt nur Bytes auf einem echten `ArrayBuffer`. */
export type Bytes = Uint8Array<ArrayBuffer>;

/** Vertrag Message: 64 KiB Klartext je Block, 16 Byte Tag, 8 Byte Nonce-Präfix. */
const BLOCK = 65536;
const TAG = 16;
const PREFIX = 8;

const nonceOf = (prefix: Bytes, counter: number): Bytes => {
  const nonce = new Uint8Array(12);
  nonce.set(prefix, 0);
  new DataView(nonce.buffer).setUint32(PREFIX, counter, false);
  return nonce;
};

/**
 * Ein Strom nach Vertrag: Präfix, dann Blöcke. Welcher der letzte ist, sagt nur die Länge —
 * ein Block ist genau dann der letzte, wenn danach nichts mehr kommt. Ein Strom, der mit einem
 * Block der AAD `0x00` endet, ist abgeschnitten; das GCM-Tag fällt dann durch.
 */
export async function decryptStream(aes: CryptoKey, bytes: Bytes): Promise<Bytes> {
  if (bytes.length < PREFIX + TAG) throw new PreviewError('decrypt', 'zu kurz');
  const prefix = bytes.subarray(0, PREFIX);
  const parts: Bytes[] = [];
  let offset = PREFIX;
  let counter = 0;
  for (;;) {
    const end = Math.min(offset + BLOCK + TAG, bytes.length);
    const last = end === bytes.length;
    try {
      const plain = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: nonceOf(prefix, counter), additionalData: new Uint8Array([last ? 1 : 0]) },
        aes,
        bytes.subarray(offset, end),
      );
      parts.push(new Uint8Array(plain));
    } catch {
      throw new PreviewError('decrypt', `Block ${counter}`);
    }
    if (last) break;
    offset = end;
    counter++;
    // Nach einem vollen Block muss noch mindestens ein Tag folgen: der (leere) Schlussblock.
    if (bytes.length - offset < TAG) throw new PreviewError('decrypt', 'Schlussblock fehlt');
  }
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

export const fromBase64 = (b64: string): Bytes => {
  const raw = atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

export const toBase64 = (bytes: Uint8Array): string => {
  let s = '';
  // In Stücken: `String.fromCharCode(...)` mit Millionen Argumenten sprengt den Stapel.
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

/** Der Umschlag: RSA-OAEP-verpackter AES-Schlüssel und, seit Message 2.0, der Eintrag im Klartext. */
export async function openEnvelope(
  keys: PreviewKeys,
  envelope: { version: number; key: string; header: PreviewHeader },
): Promise<{ aes: CryptoKey; header: PreviewHeader }> {
  if (envelope.version !== 1) throw new PreviewError('version', String(envelope.version));
  let raw: ArrayBuffer;
  try {
    raw = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, keys.decrypt, fromBase64(envelope.key));
  } catch {
    throw new PreviewError('decrypt', 'Schlüssel');
  }
  const aes = await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['decrypt']);
  const header = envelope.header;
  if (!header || !Array.isArray(header.files)) throw new PreviewError('format', 'Eintrag ohne Dateien');
  return { aes, header };
}
